import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  OutcomeType,
  UserOutcomeDetail,
} from '@app/outcomes/outcomes-api.model';
import { AchievementsModalService } from '@app/profile/components/profile-overview/achievements/achievements-modal/achievements-modal.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TranslateService } from '@ngx-translate/core';
import { Profile } from '@app/profile/profile.model';
import { FocusStackService } from '@app/shared/services/focus-stack.service';
import { AchievementsService } from '@app/profile/components/profile-overview/achievements/achievements.service';
import { Params } from '@angular/router';
import { ReactiveListVM } from '@dg/shared-rsm';
import { AchievementItem } from '@app/profile/components/profile-overview/achievements-v2/achievements.facade';
import { getEmptyTileCount as _getEmptyTileCount } from '@app/profile/components/profile-overview-v2/profile-overview.utils';

@Component({
  selector: 'dgx-profile-overview-achievements-v2',
  templateUrl: './achievements.component.html',
  styleUrls: ['./achievements.component.scss'],
})
export class ProfileOverviewAchievementsComponentV2 extends SubscriberBaseDirective {
  @Input() public profile: Profile;
  @Input() public ownerIsViewing: boolean;
  @Input() public isRestrictedView: boolean; // hides viewAll and add buttons in the skill signals page
  @Input() public achievements: ReactiveListVM<AchievementItem>;
  @Input() public isPageLoading: boolean;

  @Input() public achievementDisplayLimit: number = 3;
  @Input() public useCarousel: boolean = true;
  @Input() public useShowMore: boolean;

  @Input() public useLoadingTemplate: boolean;
  @Input() public pageType: string;
  @Input() public isMobile: boolean = false;
  @Input() public cardRowLimit: number = 3; // only in use when useCarousel is false

  @Output() public loadMoreAchievements = new EventEmitter<void>();

  @ViewChild('addMenuButton') public menuButtonRef: TemplateRef<any>;

  public viewAllUrlQueryParams: Params = { classifier: 'achievements' };
  public emptyTiles: number[] = [];
  public getEmptyTileCount = _getEmptyTileCount;

  public i18n = this.translateService.instant([
    'Core_Add',
    'Core_ViewAll',
    'Core_ViewDetails',
    'UserController_Achievements',
    'dgProfileOverview_OverviewNotProvidedTitle',
    'Core_Accomplishment',
    'Core_Award',
    'Core_Badge',
    'Core_Certificate',
    'Core_Degree',
    'dgProfileOverview_AddAchievements',
    'UserController_Type',
    'Core_CertificatesDisplayName',
    'Core_AwardsDisplayName',
    'Core_BadgesDisplayName',
    'Core_AccomplishmentsDisplayName',
    'Core_DegreesDisplayName',
    'Core_AllTypesDisplayName',
    'Core_ShowMore',
  ]);

  public menuConfig: MenuViewModel[] = [
    {
      title: this.i18n.Core_Accomplishment,
      defaultAction: (event) => this.showAddModal('Accomplishment', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Award,
      defaultAction: (event) => this.showAddModal('Award', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Certificate,
      defaultAction: (event) => this.showAddModal('Certificate', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Degree,
      defaultAction: (event) => this.showAddModal('Degree', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Badge,
      defaultAction: (event) => this.showAddModal('Badge', event),
      preventRefocus: true,
    },
  ];

  constructor(
    private translateService: TranslateService,
    private achievementsModalService: AchievementsModalService,
    private focusStackService: FocusStackService,
    private achievementsService: AchievementsService
  ) {
    super();
  }

  public get collectionUrl(): string {
    return `/profile/${this.profile.vanityUrl}/collection`;
  }

  public showAddModal(outcomeType: OutcomeType, event: Event): void {
    this.achievementsService
      .addUserOutcome(this.menuButtonRef.elementRef, outcomeType)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.focusStackService.pop();
      });
  }

  public showDetails(achievement: UserOutcomeDetail): void {
    this.achievementsModalService
      .showAchievementModal(
        this.profile.id,
        achievement.userOutcomeId,
        this.ownerIsViewing,
        !!achievement.imageUrl
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }
}
